/**
 * HQ — 홈페이지 상담 신청 (2026-10-27 대표 지시)
 *
 * newattend.com 상담 신청서로 들어온 건. 새 건 강조 → 연락 후 상태 변경·메모.
 * 개인정보 보유: 상담 종료 후 1년(신청서 동의 문구).
 */
import React, { useCallback, useEffect, useState } from 'react';
import hqConsultationService, {
  Consultation,
  ConsultationList,
  ConsultationStatus,
} from '../services/hqConsultationService';

const TYPE_LABEL: Record<string, string> = {
  franchise: '가맹 상담',
  solution: '시스템 도입',
  subscribe: '구독 문의',
  etc: '기타',
};

const STATUS_LABEL: Record<ConsultationStatus, string> = {
  new: '새 건',
  contacted: '연락함',
  done: '종료',
};

const STATUS_STYLE: Record<ConsultationStatus, string> = {
  new: 'bg-red-100 text-red-700',
  contacted: 'bg-amber-100 text-amber-700',
  done: 'bg-slate-100 text-slate-500',
};

const STATUS_ACTION: Record<ConsultationStatus, string> = {
  new: '새 건으로 되돌리기',
  contacted: '연락함으로 표시',
  done: '종료로 표시',
};

const fmtKst = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

const HqConsultationsPage: React.FC = () => {
  const [filter, setFilter] = useState<ConsultationStatus | ''>('');
  const [data, setData] = useState<ConsultationList | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Consultation | null>(null);
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const load = useCallback(() => {
    hqConsultationService
      .list(filter || undefined)
      .then((d) => {
        setData(d);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.detail || '불러오지 못했습니다'));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (c: Consultation) => {
    setSelected(c);
    setMemo(c.staff_memo || '');
    setSaveMsg('');
  };

  const save = async (status?: ConsultationStatus) => {
    if (!selected) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const updated = await hqConsultationService.update(selected.id, { status, staff_memo: memo });
      setSelected(updated);
      setSaveMsg('저장했습니다');
      load();
      window.dispatchEvent(new Event('hq-consultations-changed'));
    } catch (e: any) {
      setSaveMsg(e?.response?.data?.detail || '저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6 text-slate-500">불러오는 중…</div>;

  const { counts } = data;
  const tabs: { key: ConsultationStatus | ''; label: string; n: number }[] = [
    { key: '', label: '전체', n: counts.new + counts.contacted + counts.done },
    { key: 'new', label: '새 건', n: counts.new },
    { key: 'contacted', label: '연락함', n: counts.contacted },
    { key: 'done', label: '종료', n: counts.done },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <h1 className="text-xl sm:text-2xl font-bold mb-1">📞 상담 신청</h1>
      <p className="text-xs text-slate-500 mb-4">
        홈페이지(newattend.com) 상담 신청서로 들어온 건입니다. 연락한 뒤 상태를 바꾸고 메모를 남겨 주세요.
        <br />
        개인정보는 상담 종료 후 1년까지 보관한다고 안내했습니다.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.key || 'all'}
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              filter === t.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600'
            }`}
          >
            {t.label} {t.n}
            {t.key === 'new' && t.n > 0 && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-red-500" />}
          </button>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="bg-white rounded-xl border overflow-x-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left px-3 py-2">신청 시각</th>
                <th className="text-left px-3 py-2">유형</th>
                <th className="text-left px-3 py-2">이름</th>
                <th className="text-left px-3 py-2">연락처</th>
                <th className="text-left px-3 py-2">지역·독서실</th>
                <th className="text-left px-3 py-2">상태</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-slate-400">
                    상담 신청이 없습니다
                  </td>
                </tr>
              )}
              {data.items.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => open(c)}
                  className={`border-t cursor-pointer hover:bg-slate-50 ${
                    c.status === 'new' ? 'bg-red-50 font-semibold' : ''
                  } ${selected?.id === c.id ? 'ring-2 ring-inset ring-slate-400' : ''}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">{fmtKst(c.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{TYPE_LABEL[c.inquiry_type] || c.inquiry_type}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="text-blue-600">
                      {c.phone}
                    </a>
                    {c.phone_verified && (
                      <span className="ml-1 text-xs text-green-700" title="카카오톡 인증번호로 확인한 번호">✓ 인증</span>
                    )}
                  </td>
                  <td className="px-3 py-2 min-w-[120px]">{[c.region, c.store_name].filter(Boolean).join(' · ') || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[c.status]}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="bg-white rounded-xl border p-4 lg:w-96 text-sm space-y-2">
            <div className="flex items-center justify-between">
              <b className="text-base">{selected.name}</b>
              <button className="text-slate-400" onClick={() => setSelected(null)}>
                닫기 ✕
              </button>
            </div>
            <div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[selected.status]}`}>
                {STATUS_LABEL[selected.status]}
              </span>{' '}
              {TYPE_LABEL[selected.inquiry_type] || selected.inquiry_type}
            </div>
            <dl className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1">
              <dt className="text-slate-500">연락처</dt>
              <dd>
                <a href={`tel:${selected.phone}`} className="text-blue-600">
                  {selected.phone}
                </a>
                {selected.phone_verified ? (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-green-50 text-green-700 text-xs">휴대폰 인증됨</span>
                ) : (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">인증 전 접수</span>
                )}
              </dd>
              <dt className="text-slate-500">이메일</dt>
              <dd className="break-all">{selected.email || '-'}</dd>
              <dt className="text-slate-500">지역</dt>
              <dd>{selected.region || '-'}</dd>
              <dt className="text-slate-500">독서실</dt>
              <dd>{selected.store_name || '-'}</dd>
              <dt className="text-slate-500">좌석 수</dt>
              <dd>{selected.seat_count ? `${selected.seat_count}석` : '-'}</dd>
              <dt className="text-slate-500">신청 시각</dt>
              <dd>{fmtKst(selected.created_at)}</dd>
              <dt className="text-slate-500">개인정보</dt>
              <dd className="text-xs text-slate-500">
                동의 {fmtKst(selected.privacy_agreed_at)} · {selected.privacy_version} · IP {selected.client_ip || '-'}
              </dd>
            </dl>
            <div>
              <div className="text-slate-500 mb-1">문의 내용</div>
              <div className="whitespace-pre-wrap bg-slate-50 rounded p-2 min-h-[48px]">
                {selected.message || '(없음)'}
              </div>
            </div>
            <div>
              <div className="text-slate-500 mb-1">담당 메모</div>
              <textarea
                className="w-full border rounded p-2"
                rows={4}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="통화 내용, 다음 연락 일정 등"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                disabled={saving}
                onClick={() => save()}
                className="px-3 py-1.5 rounded bg-slate-800 text-white disabled:opacity-50"
              >
                메모 저장
              </button>
              {(['new', 'contacted', 'done'] as ConsultationStatus[])
                .filter((s) => s !== selected.status)
                .map((s) => (
                  <button
                    key={s}
                    disabled={saving}
                    onClick={() => save(s)}
                    className="px-3 py-1.5 rounded border disabled:opacity-50"
                  >
                    {STATUS_ACTION[s]}
                  </button>
                ))}
            </div>
            {saveMsg && <div className="text-xs text-slate-500">{saveMsg}</div>}
            {selected.handled_at && (
              <div className="text-xs text-slate-400">
                마지막 처리: {selected.handled_by_name || '-'} · {fmtKst(selected.handled_at)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HqConsultationsPage;
